import { useState, useEffect, useRef } from 'react';
import { 
  TrendingUp, TrendingDown, Minus, DollarSign, History, ExternalLink, 
  Loader2, RefreshCw, BarChart3, CheckCircle, AlertTriangle, Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Simple line chart component using SVG
const PriceChart = ({ history, width = 300, height = 120 }) => {
  if (!history || history.length < 2) {
    return (
      <div className="flex items-center justify-center h-24 text-zinc-500 text-sm">
        Not enough data for chart
      </div>
    );
  }

  const prices = history.map(h => h.price);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const priceRange = maxPrice - minPrice || 1;

  const padding = 10;
  const chartWidth = width - padding * 2;
  const chartHeight = height - padding * 2;

  const points = history.map((h, i) => {
    const x = padding + (i / (history.length - 1)) * chartWidth;
    const y = padding + chartHeight - ((h.price - minPrice) / priceRange) * chartHeight;
    return { x, y, ...h };
  });

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  
  // Gradient fill area
  const areaD = `${pathD} L ${points[points.length - 1].x} ${height - padding} L ${padding} ${height - padding} Z`;

  const isUp = prices[prices.length - 1] > prices[0];
  const color = isUp ? '#10b981' : '#ef4444';

  return (
    <svg width={width} height={height} className="overflow-visible">
      <defs>
        <linearGradient id={`gradient-${isUp ? 'up' : 'down'}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      
      {/* Area fill */}
      <path d={areaD} fill={`url(#gradient-${isUp ? 'up' : 'down'})`} />
      
      {/* Line */}
      <path d={pathD} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      
      {/* Data points */}
      {points.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="3" fill={color} className="opacity-0 hover:opacity-100 transition-opacity" />
      ))}
      
      {/* Price labels */}
      <text x={padding} y={padding - 2} className="text-[10px] fill-zinc-500">${maxPrice.toFixed(0)}</text>
      <text x={padding} y={height - 2} className="text-[10px] fill-zinc-500">${minPrice.toFixed(0)}</text>
    </svg>
  );
};

const RecentSalesTable = ({ cardId, listingPrice }) => {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [showChart, setShowChart] = useState(false);

  const fetchMarketValue = async (skipCache = false) => {
    if (!cardId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const cacheParam = skipCache ? '&use_cache=false' : '';
      const response = await fetch(`${API_URL}/api/market-value/${cardId}?include_external=true${cacheParam}`);
      if (!response.ok) throw new Error('Failed to fetch market data');
      const result = await response.json();
      setData(result);
      
      // Fetch price history for chart
      const historyRes = await fetch(`${API_URL}/api/market-value/${cardId}/history?days=90`);
      if (historyRes.ok) {
        const historyData = await historyRes.json();
        setHistory(historyData);
      }
    } catch (err) {
      console.error('Market value fetch error:', err);
      setError('Unable to load market data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMarketValue();
  }, [cardId]);

  const TrendIcon = ({ trend }) => {
    if (trend === 'up') return <TrendingUp className="w-4 h-4 text-emerald-400" />;
    if (trend === 'down') return <TrendingDown className="w-4 h-4 text-red-400" />;
    return <Minus className="w-4 h-4 text-zinc-400" />;
  };

  const getSourceStyle = (source, isInternal) => {
    if (isInternal) return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
    switch (source) {
      case '130 Point': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'eBay': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'COMC': return 'bg-green-500/20 text-green-400 border-green-500/30';
      default: return 'bg-zinc-700 text-zinc-300 border-zinc-600';
    }
  };

  const getPriceComparisonBadge = () => {
    if (!data?.price_vs_average || !data?.price_vs_average_pct) return null;
    
    const { price_vs_average, price_vs_average_pct } = data;
    
    if (price_vs_average === 'below') {
      return (
        <div className="flex items-center gap-1.5 px-2 py-1 bg-emerald-500/20 border border-emerald-500/30 rounded-lg">
          <Zap className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-xs font-medium text-emerald-400">
            Great Deal! {Math.abs(price_vs_average_pct)}% below avg
          </span>
        </div>
      );
    } else if (price_vs_average === 'above') {
      return (
        <div className="flex items-center gap-1.5 px-2 py-1 bg-amber-500/20 border border-amber-500/30 rounded-lg">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
          <span className="text-xs font-medium text-amber-400">
            Premium Price (+{price_vs_average_pct}%)
          </span>
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-1.5 px-2 py-1 bg-zinc-700/50 border border-zinc-600 rounded-lg">
          <CheckCircle className="w-3.5 h-3.5 text-zinc-400" />
          <span className="text-xs font-medium text-zinc-400">Fair Market Price</span>
        </div>
      );
    }
  };

  if (loading) {
    return (
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
        <div className="flex items-center gap-2 text-zinc-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading market data...</span>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-zinc-500">{error || 'No market data available'}</span>
          <button onClick={() => fetchMarketValue(true)} className="p-1.5 hover:bg-zinc-800 rounded-lg transition-colors">
            <RefreshCw className="w-4 h-4 text-zinc-500" />
          </button>
        </div>
      </div>
    );
  }

  const displaySales = expanded ? data.recent_sales : data.recent_sales.slice(0, 5);

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-800 bg-zinc-900">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-amber-400" />
            <h3 className="font-semibold text-white text-sm">Recent Sales</h3>
            {data.cached && (
              <span className="px-1.5 py-0.5 bg-zinc-700 text-zinc-400 text-[10px] rounded">Cached</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowChart(!showChart)}
              className={`p-1.5 rounded-lg transition-colors ${showChart ? 'bg-amber-500/20 text-amber-400' : 'hover:bg-zinc-800 text-zinc-500'}`}
              title="Toggle chart"
            >
              <BarChart3 className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => fetchMarketValue(true)}
              className="p-1.5 hover:bg-zinc-800 rounded-lg transition-colors"
              title="Refresh (bypass cache)"
            >
              <RefreshCw className="w-3.5 h-3.5 text-zinc-500" />
            </button>
          </div>
        </div>
        
        {/* Stats Row */}
        <div className="flex flex-wrap items-center gap-3 mt-2">
          {data.average_price && (
            <div className="flex items-center gap-1.5">
              <DollarSign className="w-3.5 h-3.5 text-zinc-500" />
              <span className="text-xs text-zinc-400">Avg:</span>
              <span className="text-sm font-medium text-white">${data.average_price.toLocaleString()}</span>
            </div>
          )}
          {data.price_trend && (
            <div className="flex items-center gap-1">
              <TrendIcon trend={data.price_trend} />
              <span className={`text-xs ${
                data.price_trend === 'up' ? 'text-emerald-400' :
                data.price_trend === 'down' ? 'text-red-400' : 'text-zinc-400'
              }`}>
                {data.price_trend === 'up' ? 'Trending Up' :
                 data.price_trend === 'down' ? 'Trending Down' : 'Stable'}
              </span>
            </div>
          )}
          {getPriceComparisonBadge()}
        </div>
      </div>

      {/* Price History Chart */}
      {showChart && history && (
        <div className="px-4 py-3 border-b border-zinc-800 bg-zinc-950/50">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-zinc-500">{history.period_days} Day Price History</span>
            {history.price_change_pct !== null && (
              <span className={`text-xs font-medium ${history.price_change_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {history.price_change_pct >= 0 ? '+' : ''}{history.price_change_pct}%
              </span>
            )}
          </div>
          <PriceChart history={history.history} width={340} height={100} />
          {history.history.length > 0 && (
            <div className="flex justify-between text-[10px] text-zinc-500 mt-1">
              <span>High: ${history.high_price?.toLocaleString()}</span>
              <span>Low: ${history.low_price?.toLocaleString()}</span>
            </div>
          )}
        </div>
      )}

      {/* Sales Table */}
      {data.recent_sales.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-xs text-zinc-500 border-b border-zinc-800">
                <th className="text-left py-2 px-4 font-medium">Date</th>
                <th className="text-left py-2 px-4 font-medium">Source</th>
                <th className="text-left py-2 px-4 font-medium">Grade</th>
                <th className="text-right py-2 px-4 font-medium">Price</th>
              </tr>
            </thead>
            <tbody>
              {displaySales.map((sale, idx) => (
                <tr key={idx} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors">
                  <td className="py-2.5 px-4">
                    <span className="text-sm text-zinc-300">{sale.date}</span>
                  </td>
                  <td className="py-2.5 px-4">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${getSourceStyle(sale.source, sale.is_internal)}`}>
                      {sale.is_internal && <span className="w-1.5 h-1.5 bg-amber-400 rounded-full" />}
                      {sale.source}
                    </span>
                  </td>
                  <td className="py-2.5 px-4">
                    <span className="text-sm text-zinc-400">{sale.grade || '—'}</span>
                  </td>
                  <td className="py-2.5 px-4 text-right">
                    <span className="text-sm font-medium text-white">${sale.price.toLocaleString()}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="px-4 py-6 text-center">
          <p className="text-sm text-zinc-500">No recent sales found for this card</p>
        </div>
      )}

      {/* Expand/Collapse */}
      {data.recent_sales.length > 5 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full py-2 text-xs text-amber-400 hover:text-amber-300 hover:bg-zinc-800/50 transition-colors border-t border-zinc-800"
        >
          {expanded ? 'Show Less' : `Show ${data.recent_sales.length - 5} More Sales`}
        </button>
      )}

      {/* Footer */}
      <div className="px-4 py-2 bg-zinc-900 border-t border-zinc-800">
        <div className="flex items-center justify-between text-xs text-zinc-500">
          <span>{data.internal_sales_count} Slab Society • {data.external_sales_count} External</span>
          <div className="flex items-center gap-1">
            <ExternalLink className="w-3 h-3" />
            <span>130 Point, eBay, COMC</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecentSalesTable;
